import * as Sentry from '@sentry/react';

export const ErrorBoundary = Sentry.ErrorBoundary;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ?? 'local',
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
    ),
    release: import.meta.env.VITE_GIT_SHA as string | undefined,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

export function setUserContext(userId: string, email?: string): void {
  Sentry.setUser({ id: userId, email });
}

export function clearUserContext(): void {
  Sentry.setUser(null);
}

export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({ message, category: 'web', level: 'info', data });
}

/**
 * Files a Sentry event when a user submits feedback, so the breadcrumb trail
 * — what they actually did in the minutes before reporting — is frozen and
 * retrievable later (YAR-365).
 *
 * Session replay is not enabled, so this is what makes a report reproducible.
 * It is also the privacy boundary: the breadcrumbs stay inside Sentry and only
 * the returned id, rendered as a link, travels to Slack.
 *
 * Returns `undefined` when Sentry was never initialised (no DSN in this
 * environment) rather than a meaningless id that would render as a dead link.
 */
export function captureFeedbackEvent(
  category: string,
  message: string,
): string | undefined {
  if (!Sentry.getClient()) return undefined;
  return Sentry.captureMessage(`User feedback: ${category}`, {
    level: 'info',
    extra: { message },
  });
}

/**
 * Files a warning-level Sentry event for something the app RECOVERED from —
 * the user was not left broken, but we still need to know it happened.
 *
 * Introduced for stale-chunk recovery (YARUTA-WEB-6 / YARUTA-WEB-7): the
 * reload repairs the screen, so without an explicit report a deploy breaking
 * live sessions would leave no trace at all.
 *
 * No-ops when Sentry was never initialised (no DSN in this environment).
 */
export function reportMessage(
  message: string,
  data?: Record<string, unknown>,
): void {
  if (!Sentry.getClient()) return;
  Sentry.captureMessage(message, { level: 'warning', extra: data });
}
